import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { IconHistory } from '@tabler/icons-react';

import { store } from 'store';
import type { WorkflowStatusId } from 'types/workflow/workflow-status';
import { findChatUserByPhone, getEffectiveWorkflowStatusForUser, WORKFLOW_OVERRIDE_EVENT } from 'utils/chat-workflow-overrides';
import { loadRecentChats, type RecentChatEntry } from 'utils/recent-chats-history-storage';
import WorkflowStatusIcon from 'views/chats/workflow/WorkflowStatusIcon';

const ICON_PX = 18;
const WF_ICON_SIZE = 16;

function resolveWorkflowStatusForRecentEntry(e: RecentChatEntry): WorkflowStatusId | null {
  if (findChatUserByPhone(e.chatUserId)) {
    return getEffectiveWorkflowStatusForUser(e.chatUserId);
  }
  const c = e.flowStatusCode;
  if (c != null && c >= 1 && c <= 6) return c;
  return null;
}

type RecentChatsHistoryButtonProps = {
  /** סרגל צד מכווץ — ללא מרווח inline-start; תפריט ממורכז מתחת לכפתור */
  compact?: boolean;
};

export default function RecentChatsHistoryButton({ compact = false }: RecentChatsHistoryButtonProps) {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [entries, setEntries] = useState<RecentChatEntry[]>(() => loadRecentChats());
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const open = Boolean(anchor);

  const tooltip = intl.formatMessage({ id: 'logoSection.recentChatsTooltip' });

  const refresh = useCallback(() => {
    setEntries(loadRecentChats());
  }, []);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setAnchor(e.currentTarget);
      refresh();
    },
    [refresh],
  );

  const handleClose = useCallback(() => {
    setAnchor(null);
  }, []);

  const emptyText = intl.formatMessage({ id: 'logoSection.recentChatsEmpty' });

  const menuHorizontal = theme.direction === 'rtl' ? 'right' : 'left';

  const menuPaperSx = useMemo(
    () => ({
      maxHeight: 320,
      minWidth: 220,
      maxWidth: 320,
    }),
    [],
  );

  useEffect(() => {
    if (!open) return;
    const unsub = store.subscribe(bump);
    const onWf = () => bump();
    window.addEventListener(WORKFLOW_OVERRIDE_EVENT, onWf);
    return () => {
      unsub();
      window.removeEventListener(WORKFLOW_OVERRIDE_EVENT, onWf);
    };
  }, [open]);

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={tooltip}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            padding: 0.5,
            marginInlineStart: 0,
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <IconHistory size={ICON_PX} stroke={1.75} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: { sx: menuPaperSx },
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: compact ? 'center' : menuHorizontal }}
        transformOrigin={{ vertical: 'top', horizontal: compact ? 'center' : menuHorizontal }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, pt: 1, pb: 0.5, display: 'block' }}
        >
          {intl.formatMessage({ id: 'logoSection.recentChatsTitle' })}
        </Typography>
        {entries.length === 0 ? (
          <MenuItem disabled sx={{ opacity: 0.8 }}>
            <ListItemText primary={emptyText} primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        ) : (
          entries.map((e) => {
            const wf = resolveWorkflowStatusForRecentEntry(e);
            return (
              <MenuItem
                key={e.chatUserId}
                dense
                onClick={() => {
                  navigate(e.to);
                  handleClose();
                }}
                sx={{ alignItems: 'flex-start', py: 1 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {wf != null && (
                    <Box sx={{ flexShrink: 0, pt: 0.125 }}>
                      <WorkflowStatusIcon status={wf} size={WF_ICON_SIZE} />
                    </Box>
                  )}
                  <ListItemText
                    primary={e.label}
                    secondary={e.chatUserId}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                      sx: { maxWidth: 260 },
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      noWrap: true,
                      sx: { color: theme.palette.text.secondary },
                    }}
                  />
                </Box>
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
}
